import {
  CourtRoom,
  MemberBalance,
  Settlement,
  Currency,
  CURRENCY_SYMBOLS,
} from '../types';
import { BalanceCalculator } from './BalanceCalculator';

/**
 * ExportService — Generates CSV exports and text summaries for Court Splitter rooms.
 */
export class ExportService {
  /**
   * Export sessions as a CSV string.
   * Columns: Date, Cost, Paid By, Players, Per Person, Note
   */
  static exportSessionsCSV(room: CourtRoom): string {
    const symbol = CURRENCY_SYMBOLS[room.currency] || '$';
    const headers = ['Date', 'Cost', 'Paid By', 'Players', 'Per Person', 'Note'];
    const rows: string[][] = [];

    const sortedSessions = [...room.sessions].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    for (const session of sortedSessions) {
      const payer = room.members.find(m => m.id === session.paidBy);
      const players = session.attendees
        .map(id => room.members.find(m => m.id === id)?.name || 'Unknown')
        .join('; ');
      const perPerson = session.attendees.length > 0
        ? (session.totalCost / session.attendees.length).toFixed(2)
        : '0.00';

      rows.push([
        session.date,
        `${symbol}${session.totalCost.toFixed(2)}`,
        payer?.name || 'Unknown',
        players,
        `${symbol}${perPerson}`,
        session.note || '',
      ]);
    }

    // Build CSV
    const csvLines = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')),
    ];

    return csvLines.join('\n');
  }

  /**
   * Export balances summary as plain text (for sharing via WhatsApp/clipboard).
   */
  static exportBalancesSummary(room: CourtRoom): string {
    const balances = BalanceCalculator.calculateBalances(room);
    const settlements = BalanceCalculator.calculateSettlements(room);
    const stats = BalanceCalculator.getSummaryStats(room);
    const symbol = CURRENCY_SYMBOLS[room.currency] || '$';

    const lines: string[] = [
      `🏸 ${room.name} — Balance Summary`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      '',
      `📊 Stats:`,
      `  Sessions: ${stats.totalSessions}`,
      `  Total spent: ${symbol}${stats.totalSpent.toFixed(2)}`,
      `  Avg per session: ${symbol}${stats.averagePerSession.toFixed(2)}`,
      `  Avg per person: ${symbol}${stats.averagePerPerson.toFixed(2)}`,
      '',
      `💰 Balances:`,
    ];

    for (const b of balances) {
      const sign = b.netBalance >= 0 ? '+' : '';
      lines.push(`  ${b.memberName}: ${sign}${symbol}${b.netBalance.toFixed(2)}`);
    }

    if (settlements.length > 0) {
      lines.push('');
      lines.push('🤝 To settle up:');
      for (const s of settlements) {
        lines.push(`  ${s.fromMemberName} → ${symbol}${s.amount.toFixed(2)} → ${s.toMemberName}`);
      }
    }

    lines.push('');
    lines.push(`Generated from PlayHub Court Splitter`);

    return lines.join('\n');
  }

  /**
   * Trigger a CSV file download in the browser.
   */
  static downloadCSV(room: CourtRoom): void {
    const csv = this.exportSessionsCSV(room);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${room.name.replace(/[^a-zA-Z0-9]/g, '-')}-sessions.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Copy balance summary to clipboard.
   */
  static async copyBalanceSummary(room: CourtRoom): Promise<boolean> {
    const text = this.exportBalancesSummary(room);
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
}
